import React, { useState } from 'react';
import { Card, Form, Button, Upload, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { fileUpload } from '../../../../../utils/storageUtils';
import {
    IMPORT_DEFAULT_ZIPCODE_PICKUP_HUB_CONFIG,
    ImportDefaultZipcodePickupHubConfigVariables,
    ImportDefaultZipcodePickupHubConfigResponse
} from '../graphql/mutations/defaultZipcodePickupHubMutations';

const { Dragger } = Upload;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const ImportTab: React.FC = () => {
    const [form] = Form.useForm();
    const [uploading, setUploading] = useState(false);
    const [fileList, setFileList] = useState<any[]>([]);

    const [importDefaultZipcodePickupHubConfig] = useMutation<
        ImportDefaultZipcodePickupHubConfigResponse,
        ImportDefaultZipcodePickupHubConfigVariables
    >(IMPORT_DEFAULT_ZIPCODE_PICKUP_HUB_CONFIG);

    const handleUpload = async (file: File) => {
        if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
            message.error('请上传Excel文件（.xlsx, .xls）！');
            return false;
        }

        if (file.size > MAX_FILE_SIZE) {
            message.error(`文件大小不能超过20MB，当前文件大小：${(file.size / 1024 / 1024).toFixed(2)}MB`);
            return false;
        }

        setFileList([{
            uid: Date.now().toString(),
            name: file.name,
            status: 'done',
            originFileObj: file
        }]);

        return false;
    };

    const handleSubmit = async () => {
        try {
            if (fileList.length === 0) {
                message.error('请先上传文件！');
                return;
            }

            setUploading(true);

            const fileObj = fileList[0].originFileObj;
            const { objectKey } = await fileUpload(fileObj, 'default-zipcode-pickup-hub');

            const result = await importDefaultZipcodePickupHubConfig({
                variables: {
                    input: {
                        objectKey,
                        filename: fileObj.name
                    }
                }
            });

            if (result.data?.importDefaultZipcodePickupHubConfig) {
                const response = result.data.importDefaultZipcodePickupHubConfig;
                if (response.success) {
                    message.success(
                        `导入成功！共处理 ${response.totalCount} 条记录，成功 ${response.importedCount} 条${response.failedCount > 0 ? `，失败 ${response.failedCount} 条` : ''}`
                    );
                    form.resetFields();
                    setFileList([]);
                } else {
                    message.error(`导入失败：${response.message}`);
                }
            }
        } catch (error: any) {
            message.error(error.message || '提交失败');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="p-6">
            <Card
                title="导入默认ZipCode揽收HUB配置"
                className="max-w-4xl mx-auto"
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        label="上传Excel文件"
                        required
                        tooltip="文件最大支持20MB，格式要求：包含zipcode和hubId两列，不允许重复的zipcode"
                        extra={
                            <div className="mt-2 space-y-2">
                                <div className="text-sm text-gray-600">
                                    <div className="font-semibold mb-1">📋 Excel文件格式要求：</div>
                                    <div className="pl-4 space-y-1 text-xs">
                                        <div>1. <strong>表头格式</strong>：必须包含以下两列（不区分大小写，支持前10行自动检测）</div>
                                        <div className="pl-4 space-y-1">
                                            <div>• <span className="font-mono bg-gray-100 px-1 rounded">zipcode</span> 列（支持：<span className="font-mono">zipcode</span>、<span className="font-mono">zip code</span>、<span className="font-mono">zip</span>）</div>
                                            <div>• <span className="font-mono bg-gray-100 px-1 rounded">hubId</span> 列（支持：<span className="font-mono">hubId</span>、<span className="font-mono">hub_id</span>、<span className="font-mono">hub</span>）</div>
                                        </div>
                                        <div>2. <strong>数据格式要求</strong>：</div>
                                        <div className="pl-4 space-y-1">
                                            <div>• <span className="font-mono">zipcode</span>：必须是有效的5位数字（如：12345）</div>
                                            <div>• <span className="font-mono">hubId</span>：必须是数字（如：1, 2, 100）</div>
                                            <div>• <span className="text-red-600 font-semibold">不允许出现重复的zipcode</span></div>
                                            <div>• <span className="text-red-600 font-semibold">zipcode或hubId不能为空</span></div>
                                        </div>
                                        <div>3. <strong>示例格式</strong>：</div>
                                        <div className="pl-4">
                                            <table className="border-collapse border border-gray-300 text-xs mt-1">
                                                <thead>
                                                <tr className="bg-gray-100">
                                                    <th className="border border-gray-300 px-2 py-1">zipcode</th>
                                                    <th className="border border-gray-300 px-2 py-1">hubId</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr>
                                                    <td className="border border-gray-300 px-2 py-1">12345</td>
                                                    <td className="border border-gray-300 px-2 py-1">1</td>
                                                </tr>
                                                <tr>
                                                    <td className="border border-gray-300 px-2 py-1">67890</td>
                                                    <td className="border border-gray-300 px-2 py-1">2</td>
                                                </tr>
                                                <tr>
                                                    <td className="border border-gray-300 px-2 py-1">11111</td>
                                                    <td className="border border-gray-300 px-2 py-1">3</td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <Dragger
                            fileList={fileList}
                            beforeUpload={handleUpload}
                            onRemove={() => setFileList([])}
                            maxCount={1}
                            accept=".xlsx,.xls"
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                            <p className="ant-upload-hint">
                                支持 .xlsx 和 .xls 格式，文件大小不超过20MB
                            </p>
                        </Dragger>
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            size="large"
                            loading={uploading}
                            onClick={handleSubmit}
                            block
                        >
                            提交导入
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default ImportTab;
